/* JRU-458: Shopify renders the data once; filtering and swatches make no requests. */
(() => {
  const normalizeColor = value => (value || '').toLowerCase()
    .replace(/\bmetallic\b/g, '').replace(/&amp;/g, '&').replace(/\s+/g, ' ').trim().replace(/^all white$/, 'white');
  const colorKey = product => normalizeColor(product.swatch.color || product.swatch.label);
  const isChild = product => /\b(girls?|kids?|toddler|baby)\b/i.test(product.swatch.handle + ' ' + product.title);
  const styleOf = product => product.swatch.handle.includes('laurin') ? 'laurin'
    : product.swatch.handle.includes('jacks-eva-') ? 'waterproof' : /^(?:girls-)?jacks?-flat/.test(product.swatch.handle) ? 'original' : null;
  const selectedValues = value => Array.isArray(value) ? value : value ? [value] : [];
  const matches = (value, selected) => !selectedValues(selected).length || selectedValues(selected).includes(value);
  const availableVariants = (product, size = '') => product.variants.filter(variant =>
    variant.available && matches(variant.size, size) && Number.isFinite(variant.price));
  const allowedColors = {
    laurin: ['platinum', 'white', 'rose quartz'],
    waterproof: ['pale pink', 'cream', 'fuchsia', 'navy', 'bright yellow', 'lilac']
  };
  const bundleViews = ['sun-sea-jacks-bundle', 'sun-sea-laurin-bundle', 'sun-sea-collins-bundle', 'land-shore-bundle', 'jacks-laurin-bundle', 'wedding-weekend-bundle'];
  const pairDestination = destination => {
    try {
      const origin = typeof location === 'undefined' ? 'https://store.invalid' : location.origin;
      const url = new URL(destination, origin);
      return url.origin === origin && /\/products\/[^/]+$/.test(url.pathname) &&
        url.searchParams.getAll('view').length === 1 && bundleViews.includes(url.searchParams.get('view')) ? url : null;
    } catch (_) { return null; }
  };
  const familyProducts = (products, colors) => {
    const allowed = (colors || '').split(',').map(normalizeColor).filter(Boolean);
    const unique = [...new Map(products.map(product => [product.swatch.handle, product])).values()];
    const eligible = unique.filter(product => !isChild(product) && colorKey(product) &&
      (!allowed.length || allowed.includes(colorKey(product))) && availableVariants(product).length);
    // A family must not silently pick between different products with the same color.
    return eligible.filter(product => eligible.filter(other => colorKey(other) === colorKey(product)).length === 1);
  };
  const familyPairs = card => {
    if (!pairDestination(card.destination)) return [];
    const adults = familyProducts(card.adults, card.firstColors);
    const partners = familyProducts(card.children, card.secondColors);
    const pairs = [];
    const add = (adult, child) => {
      if (!adult || !child || adult.swatch.handle === child.swatch.handle) return;
      const key = adult.swatch.handle + '+' + child.swatch.handle;
      if (!pairs.some(pair => pair.key === key)) pairs.push({ key, adult, child, explicit: true, family: true });
    };
    // Start with the configured editorial pairing, then offer matching-color previews.
    // Independent color combinations belong to the PDP, not a generated PLP cross-product.
    const featuredAdult = adults.find(p => p.swatch.handle === card.adults[0]?.swatch.handle);
    const featuredPartner = partners.find(p => p.swatch.handle === card.children[0]?.swatch.handle);
    if (card.adults[0] && card.children[0] && (!featuredAdult || !featuredPartner)) {
      console.warn('Configured bundle pair is excluded by the family filters:', card.title);
    }
    add(featuredAdult, featuredPartner);
    adults.forEach(adult => add(adult, partners.find(child => colorKey(child) === colorKey(adult))));
    return pairs;
  };
  const pairLabel = pair => (pair.explicit ? [pair.adult, pair.child] : [pair.adult])
    .map(product => product.swatch.label.replace(/^Metallic /i, '')).filter((label, index, labels) => labels.indexOf(label) === index).join(' + ');
  const pairsFor = card => {
    if (card.pairType === 'families') return familyPairs(card);
    // Explicit pairs never expand families into unverified combinations.
    if (card.pairType === 'explicit') {
      const [adult] = card.adults;
      const [child] = card.children;
      if (!pairDestination(card.destination) || !adult || !child || adult.swatch.handle === child.swatch.handle ||
          isChild(adult) || isChild(child) || !availableVariants(adult).length || !availableVariants(child).length) return [];
      return [{ key: adult.swatch.handle + '+' + child.swatch.handle, adult, child, explicit: true }];
    }
    const seen = new Set();
    return card.adults.flatMap(adult => {
      const key = colorKey(adult);
      if (!key || seen.has(key) || isChild(adult) || styleOf(adult) !== card.style ||
          !availableVariants(adult).length || (allowedColors[card.style] && !allowedColors[card.style].includes(key))) return [];
      const children = card.children.filter(child => isChild(child) && styleOf(child) === card.style &&
        colorKey(child) === key && availableVariants(child).length);
      // Ambiguous family entries must be curated in Shopify rather than picked arbitrarily.
      if (children.length !== 1 || card.adults.filter(p => colorKey(p) === key && !isChild(p) &&
          styleOf(p) === card.style && availableVariants(p).length).length !== 1) return [];
      seen.add(key);
      return [{ key, adult, child: children[0] }];
    });
  };
  const sizeFor = (pair, filters) => pair.explicit ? '' : filters.childSize;
  const colorsFor = pair => [...new Set([colorKey(pair.adult), colorKey(pair.child)].filter(Boolean))];
  const matchingPairs = (card, filters) => card.pairs.filter(pair =>
    matches(card.category, filters.category) &&
    (!selectedValues(filters.color).length || colorsFor(pair).some(color => matches(color, filters.color))) &&
    availableVariants(pair.adult, filters.adultSize).length &&
    (!pair.explicit || !selectedValues(filters.childSize).length) &&
    availableVariants(pair.child, sizeFor(pair, filters)).length);
  const priceFor = (pair, filters = {}, promotionConfig) => {
    const prices = [availableVariants(pair.adult, filters.adultSize), availableVariants(pair.child, sizeFor(pair, filters))]
      .map(variants => variants.map(v => v.price));
    if (prices.some(values => !values.length)) return null;
    const regular = { min: Math.min(...prices[0]) + Math.min(...prices[1]), max: Math.max(...prices[0]) + Math.max(...prices[1]) };
    const quote = boundary => pair.explicit && globalThis.Jru461BundlePromotion?.quote(promotionConfig, [pair.adult, pair.child].map((product, i) => ({ productId: product.id, priceCents: Math[boundary](...prices[i]) })));
    const low = quote('min');
    const high = quote('max');
    const eva = !pair.explicit && globalThis.Jru450BundlePromotion?.quote(pair.evaPromotion, [pair.adult, pair.child].map((product, i) => ({ productId: product.id || product.swatch.id, priceCents: Math.min(...prices[i]) })));
    if (eva) return { min: eva.totalCents, max: eva.totalCents, promotion: true };
    return low && high ? { min: low.totalCents, max: high.totalCents, regularMin: regular.min } : regular;
  };
  const destination = (root, card, pair) => {
    if (card.pairType === 'explicit' || card.pairType === 'families') {
      const url = pairDestination(card.destination);
      if (!url) return null;
      url.pathname = (root.replace(/\/$/, '') + '/products/' + url.pathname.split('/products/').pop());
      url.searchParams.set('bundle_first', pair.adult.swatch.handle);
      url.searchParams.set('bundle_second', pair.child.swatch.handle);
      return url.pathname + url.search + url.hash;
    }
    // Always enter through a verified trigger. In particular, Laurin Platinum uses Girls White.
    const url = new URL(root.replace(/\/$/, '') + '/products/' + card.trigger, 'https://store.invalid');
    url.searchParams.set('view', card.view);
    url.searchParams.set('bundle_color', pair.key.replace(/ /g, '-'));
    return url.pathname + url.search;
  };
  const sortCards = (cards, sort, filters, promotionConfig) => [...cards].sort((a, b) => {
    if (sort === 'price-ascending' || sort === 'price-descending') {
      return (priceFor(a.selected, filters, promotionConfig).min - priceFor(b.selected, filters, promotionConfig).min) * (sort === 'price-descending' ? -1 : 1) || a.index - b.index;
    }
    if (sort === 'title-ascending' || sort === 'title-descending') {
      return a.title.localeCompare(b.title) * (sort === 'title-descending' ? -1 : 1) || a.index - b.index;
    }
    return a.index - b.index;
  });
  if (typeof module !== 'undefined' && module.exports) module.exports = { colorKey, pairsFor, matchingPairs, priceFor, destination, sortCards };
  if (typeof HTMLElement === 'undefined' || customElements.get('mommy-me-plp')) return;
  const make = (tag, text, attrs = {}) => {
    const node = document.createElement(tag);
    if (text != null) node.textContent = text;
    Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, value));
    return node;
  };
  class MommyMePLP extends HTMLElement {
    connectedCallback() {
      this.abort?.abort();
      this.abort = new AbortController();
      try {
        this.sort = '';
        this.config = JSON.parse(this.querySelector('[data-plp-config]').textContent);
        this.cards = [...this.querySelectorAll('[data-plp-card]')].map((node, index) => {
          const card = { ...JSON.parse(node.querySelector('[data-card-config]').textContent), node, index };
          [...card.adults, ...card.children].forEach(product => {
            const decoder = document.createElement('textarea');
            decoder.innerHTML = product.swatch.label;
            product.swatch.label = decoder.value;
          });
          card.pairs = pairsFor(card);
          card.pairs.forEach(pair => { pair.evaPromotion = this.config.evaPromotion; });
          card.choice = card.pairType === 'families' ? card.pairs[0]?.key : colorKey(card.adults[0]);
          card.swatches = new Map();
          const row = node.querySelector('[data-swatches]');
          row.replaceChildren();
          row.hidden = card.pairType === 'explicit';
          card.pairs.filter(pair => !pair.explicit || pair.family).forEach(pair => {
            const label = pair.family ? pairLabel(pair) : pair.key + ' for both shoes';
            const button = make('button', null, { type: 'button', class: 'mm-plp__swatch',
              'aria-label': label, 'aria-pressed': 'false', title: label });
            const products = pair.family && colorKey(pair.adult) !== colorKey(pair.child) ? [pair.adult, pair.child] : [pair.adult];
            if (products.length === 2) button.classList.add('mm-plp__swatch--pair');
            products.forEach(product => {
              const dot = make('span', null, { 'data-swatch': colorKey(product), 'aria-hidden': 'true' });
              const swatch = product.swatch;
              dot.dataset.swatch = swatch.color || colorKey(product);
              if (swatch.swatchImage) dot.style.backgroundImage = 'url("' + swatch.swatchImage.replace(/["\\]/g, '') + '")';
              else if (swatch.swatchColor) dot.style.backgroundColor = 'rgb(' + swatch.swatchColor + ')';
              button.append(dot);
            });
            button.addEventListener('click', () => {
              card.choice = pair.key;
              this.update();
              this.querySelector('[data-plp-announcement]').textContent = card.title + ': ' + label + (pair.family ? ' preview selected. Choose colors on the product page.' : ' selected.');
            }, { signal: this.abort.signal });
            row.append(button);
            card.swatches.set(pair.key, button);
          });
          return card;
        });
        this.populateFilters();
        this.setupControls();
        this.update();
        const toolbar = this.querySelector('[data-plp-toolbar]');
        if (toolbar) toolbar.hidden = false;
      } catch {
        this.querySelector('[data-plp-announcement]').textContent = 'Filters could not load. You can still open the available bundles below.';
      }
    }
    disconnectedCallback() {
      this.closeFilters(false);
      this.abort?.abort();
    }
    setupControls() {
      const options = { signal: this.abort.signal };
      this.drawer = this.querySelector('[data-plp-filters]');
      this.opener = this.querySelector('[data-open-filters]');
      this.opener?.addEventListener('click', () => {
        if (!this.drawer || this.drawer.open) return;
        this.previousOverflow = document.body.style.overflow;
        this.drawer.showModal();
        document.body.style.overflow = 'hidden';
        this.opener.setAttribute('aria-expanded', 'true');
      }, options);
      this.querySelectorAll('[data-close-filters]').forEach(button =>
        button.addEventListener('click', () => this.closeFilters(), options));
      this.drawer?.addEventListener('close', () => { if (!this.drawer.open) this.restoreDrawerState(); }, options);
      this.drawer?.addEventListener('cancel', event => {
        event.preventDefault();
        this.closeFilters();
      }, options);
      this.drawer?.addEventListener('click', event => {
        if (event.target !== this.drawer) return;
        const bounds = this.drawer.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right ||
            event.clientY < bounds.top || event.clientY > bounds.bottom) this.closeFilters();
      }, options);
      this.addEventListener('change', event => {
        if (event.target.matches('input[data-filter]')) this.update();
      }, options);
      this.querySelector('[data-clear]')?.addEventListener('click', () => {
        this.querySelectorAll('input[data-filter]').forEach(input => { input.checked = false; });
        this.update();
        // Clearing hides the focused reset button; keep keyboard focus inside the drawer.
        this.drawer?.querySelector('[data-close-filters]')?.focus({ preventScroll: true });
      }, options);
      this.querySelectorAll('[data-sort]').forEach(button => button.addEventListener('click', () => {
        this.sort = button.dataset.sort;
        const menu = button.closest('[data-sort-menu]');
        if (menu) { menu.open = false; menu.querySelector('summary').focus(); }
        this.update();
      }, options));
      const menu = this.querySelector('[data-sort-menu]');
      document.addEventListener('click', event => {
        if (menu && !menu.contains(event.target)) menu.open = false;
      }, options);
      menu?.addEventListener('keydown', event => {
        if (event.key === 'Escape') {
          menu.open = false;
          menu.querySelector('summary').focus();
        }
      }, options);
    }
    closeFilters(returnFocus = true) {
      if (!this.drawer?.open) return;
      this.drawer.close();
      this.restoreDrawerState(returnFocus);
    }
    restoreDrawerState(returnFocus = true) {
      if (this.previousOverflow === undefined) return;
      document.body.style.overflow = this.previousOverflow;
      this.previousOverflow = undefined;
      this.opener?.setAttribute('aria-expanded', 'false');
      if (returnFocus && this.isConnected) this.opener?.focus();
    }
    populateFilters() {
      const values = { category: new Set(), color: new Set(), adultSize: new Set(), childSize: new Set() };
      const colorSwatches = new Map();
      this.cards.forEach(card => {
        if (card.pairs.length) values.category.add(card.category);
        card.pairs.forEach(pair => {
          [pair.adult, pair.child].forEach(product => {
            const color = colorKey(product);
            if (color) {
              values.color.add(color);
              if (!colorSwatches.has(color)) colorSwatches.set(color, product.swatch);
            }
          });
          availableVariants(pair.adult).forEach(v => {
            if (v.size) values.adultSize.add(v.size);
          });
          if (!pair.explicit) availableVariants(pair.child).forEach(v => { if (v.size) values.childSize.add(v.size); });
        });
      });
      Object.entries(values).forEach(([name, entries]) => {
        const container = this.querySelector('[data-filter-options="' + name + '"]');
        if (!container) return;
        container.closest('[data-filter-group]').hidden = !entries.size;
        container.replaceChildren();
        [...entries].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).forEach(value => {
          const label = make('label', null, { class: 'mm-plp__choice' });
          const input = make('input', null, { type: 'checkbox', 'data-filter': name, value });
          const text = make('span', value);
          if (name === 'color') {
            const swatch = colorSwatches.get(value);
            const dot = make('span', null, { class: 'mm-plp__filter-swatch', 'data-swatch': swatch.color || value, 'aria-hidden': 'true' });
            if (swatch.swatchImage) dot.style.backgroundImage = 'url("' + swatch.swatchImage.replace(/["\\]/g, '') + '")';
            else if (swatch.swatchColor) dot.style.backgroundColor = 'rgb(' + swatch.swatchColor + ')';
            text.prepend(dot);
          }
          label.append(input, text);
          container.append(label);
        });
      });
    }
    filters() {
      const values = { sort: this.sort };
      this.querySelectorAll('input[data-filter]:checked').forEach(input => {
        (values[input.dataset.filter] ||= []).push(input.value);
      });
      return values;
    }
    updateControls(filters, visibleCount) {
      const selected = [...this.querySelectorAll('input[data-filter]:checked')];
      const clear = this.querySelector('[data-clear]');
      if (clear) clear.hidden = !selected.length;
      const count = this.querySelector('[data-filter-count]');
      if (count) { count.hidden = !selected.length; count.textContent = '(' + selected.length + ')'; }
      const active = this.querySelector('[data-active-filters]');
      if (active) {
        active.replaceChildren();
        selected.forEach(input => {
          const name = input.closest('fieldset').querySelector('legend').textContent;
          const chip = make('button', name + ': ' + input.value + ' ×', {
            type: 'button', class: 'mm-plp__active-filter', 'aria-label': 'Remove ' + name + ': ' + input.value
          });
          chip.addEventListener('click', () => {
            input.checked = false;
            this.update();
            input.focus();
          }, { signal: this.abort.signal });
          active.append(chip);
        });
      }
      this.querySelectorAll('[data-sort]').forEach(button =>
        button.setAttribute('aria-pressed', String(button.dataset.sort === filters.sort)));
      const selectedSort = [...this.querySelectorAll('[data-sort]')].find(button => button.dataset.sort === filters.sort);
      const sortLabel = this.querySelector('[data-sort-label]');
      if (sortLabel && selectedSort) sortLabel.textContent = selectedSort.textContent;
      const show = this.querySelector('.mm-plp__show-results');
      if (show) show.textContent = 'Show ' + visibleCount + (visibleCount === 1 ? ' bundle' : ' bundles');
    }
    update() {
      const active = document.activeElement;
      const filters = this.filters();
      const visible = [];
      this.cards.forEach(card => {
        const pairs = matchingPairs(card, filters);
        card.node.hidden = !pairs.length;
        if (!pairs.length) return;
        card.selected = pairs.find(pair => pair.key === card.choice) || pairs[0];
        this.renderCard(card, pairs, filters);
        visible.push(card);
      });
      const grid = this.querySelector('[data-plp-grid]');
      sortCards(visible, filters.sort, filters, this.config.bundleSavings).forEach(card => grid.append(card.node));
      this.querySelectorAll('[data-plp-count]').forEach(node => { node.textContent = visible.length + (visible.length === 1 ? ' bundle' : ' bundles'); });
      this.querySelector('[data-plp-empty]').hidden = !!visible.length;
      this.updateControls(filters, visible.length);
      if (active && this.contains(active) && !active.closest('[hidden]')) active.focus({ preventScroll: true });
    }
    renderCard(card, pairs, filters) {
      const pair = card.selected;
      const url = destination(this.config.root, card, pair);
      card.node.querySelectorAll('[data-bundle-link]').forEach(link => { link.href = url; });
      const price = priceFor(pair, filters, this.config.bundleSavings);
      const format = cents => new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.config.currency }).format(cents / 100);
      const promotionNote = card.node.querySelector('[data-eva-promotion]');
      if (promotionNote) promotionNote.hidden = !price.promotion;
      const priceNode = card.node.querySelector('[data-price]');
      priceNode.textContent = (pair.explicit ? 'Products total: ' : '') + (price.min !== price.max ? 'From ' : '') + format(price.min);
      if (price.regularMin !== undefined) priceNode.append(' ', make('del', format(price.regularMin)), make('span', '20% off bundle', { class: 'mm-plp__savings' }));
      card.node.querySelector('[data-color]').textContent = pairLabel(pair);
      const stage = card.node.querySelector('[data-images]');
      if (stage.dataset.imageColor !== pair.key) {
        stage.replaceChildren();
        // Always show the two selected products, including when returning to the starting color.
        [pair.adult, pair.child].forEach(product => {
          if (product.image) stage.append(make('img', null, { src: product.image, alt: product.title + ' in ' + colorKey(product), loading: 'lazy', width: '800', height: '800' }));
        });
        stage.dataset.imageColor = pair.key;
      }
      card.swatches.forEach((button, key) => {
        button.hidden = !pairs.some(candidate => candidate.key === key);
        button.setAttribute('aria-pressed', String(key === pair.key));
      });
    }
  }
  customElements.define('mommy-me-plp', MommyMePLP);
})();
